import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Tooltip,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  Button,
  Avatar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Menu as MenuIcon,
  ViewColumn,
  Brightness4,
  Brightness7,
  MoreVert,
  ArrowBack,
} from "@mui/icons-material";
import { Search, Clock, BellIcon, RefreshCw } from "lucide-react";
import { ListBulletIcon } from "@radix-ui/react-icons";
import { getSystemDetails } from "../../utils/formatting";

function TopBar({
  isMobile,
  setSidebarOpen,
  activeView,
  sidebarItems,
  darkMode,
  setDarkMode,
  theme,
  globalSearch,
  setGlobalSearch,
  globalSearchAnchor,
  setGlobalSearchAnchor,
  globalSearchRef,
  licenses,
  systems,
  cameras,
  setActiveView,
  setSearchTerm,
  splitScreen,
  setSplitScreen,
  refreshing,
  fetchAllData,
  autoRefresh,
  setAutoRefresh,
  autoRefreshInterval,
  setAutoRefreshInterval,
  autoRefreshAnchor,
  setAutoRefreshAnchor,
  notifAnchor,
  setNotifAnchor,
  notifCount,
  expiringCount,
  offlineCameras,
  currentUser,
  fetchSessionLogs,
}) {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    setGlobalSearch('');
    setGlobalSearchAnchor(null);
  };

  const searchResults = (() => {
    if (!globalSearch || !globalSearchAnchor) return null;
    const q = globalSearch.toLowerCase();
    const matchedLicenses = licenses.filter(l =>
      l.license_id?.toLowerCase().includes(q) || l.uuid?.toLowerCase().includes(q)
    ).slice(0, 3);
    const matchedSystems = systems.filter(s =>
      s.uuid?.toLowerCase().includes(q) || s.project_id?.toLowerCase().includes(q)
    ).slice(0, 3);
    const matchedCameras = cameras.filter(c =>
      c.camera_serial?.toLowerCase().includes(q) || c.uuid?.toLowerCase().includes(q)
    ).slice(0, 3);
    const hasResults = matchedLicenses.length > 0 || matchedSystems.length > 0 || matchedCameras.length > 0;
    if (!hasResults) return null;

    const goTo = (view, term) => {
      setActiveView(view);
      setSearchTerm(term);
      setGlobalSearch('');
      setGlobalSearchAnchor(null);
      setMobileSearchOpen(false);
    };

    return (
      <Paper
        elevation={8}
        sx={{
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? 56 : '100%',
          left: isMobile ? 0 : 0,
          right: isMobile ? 0 : 0,
          maxWidth: isMobile ? '100%' : 420,
          mt: isMobile ? 0 : 0.5,
          zIndex: 9999,
          maxHeight: isMobile ? 'calc(100vh - 56px)' : 400,
          overflow: 'auto',
          bgcolor: darkMode ? '#0E1626' : 'background.paper',
          border: darkMode ? '1px solid #16213A' : '1px solid #D8E1E8',
        }}
      >
        {matchedLicenses.length > 0 && (
          <>
            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Licenses ({matchedLicenses.length})
            </Typography>
            {matchedLicenses.map(l => (
              <Box
                key={l.license_id}
                sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onMouseDown={() => goTo('licenses', l.license_id)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>{l.license_id}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{l.uuid?.substring(0, 12)}...</Typography>
              </Box>
            ))}
            <Divider />
          </>
        )}
        {matchedSystems.length > 0 && (
          <>
            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Systems ({matchedSystems.length})
            </Typography>
            {matchedSystems.map(s => (
              <Box
                key={s.uuid}
                sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onMouseDown={() => goTo('systems', s.uuid)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>{s.uuid?.substring(0, 12)}...</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{s.project_id}</Typography>
              </Box>
            ))}
            <Divider />
          </>
        )}
        {matchedCameras.length > 0 && (
          <>
            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Cameras ({matchedCameras.length})
            </Typography>
            {matchedCameras.map(c => (
              <Box
                key={c.id}
                sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                onMouseDown={() => goTo('cameras', c.camera_serial)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>{c.camera_serial}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.uuid?.substring(0, 12)}...</Typography>
              </Box>
            ))}
          </>
        )}
      </Paper>
    );
  })();

  return (
    <AppBar position="sticky" sx={{
      bgcolor: 'transparent',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255,255,255,0.72)',
      borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      boxShadow: darkMode ? '0 4px 32px rgba(0,0,0,0.4)' : '0 4px 32px rgba(0,0,0,0.04)',
    }}>
      {isMobile && mobileSearchOpen ? (
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={closeMobileSearch} sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }} ref={globalSearchRef}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder="Search licenses, systems, cameras..."
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);
                setGlobalSearchAnchor(e.target.value ? globalSearchRef.current : null);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} color={darkMode ? '#93A2BC' : undefined} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Toolbar>
      ) : (
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" onClick={() => setSidebarOpen(true)} sx={{ mr: 1, color: darkMode ? '#EAF3FB' : 'inherit' }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap sx={{ flex: isMobile ? '0 1 auto' : 0, fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
            {sidebarItems.find(i => i.view === activeView)?.text ||
              (activeView === 'projectDetails' ? 'Project Details' :
                activeView === 'users' ? 'User Management' : 'Dashboard')}
          </Typography>

          {!isMobile && (
            <Box sx={{ flex: 1, mx: 3, position: 'relative' }} ref={globalSearchRef}>
              <TextField
                size="small"
                placeholder="Search licenses, systems, cameras..."
                value={globalSearch}
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setGlobalSearchAnchor(e.target.value ? globalSearchRef.current : null);
                }}
                onBlur={() => setTimeout(() => setGlobalSearchAnchor(null), 200)}
                sx={{ width: '100%', maxWidth: 420 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color={darkMode ? '#93A2BC' : undefined} />
                    </InputAdornment>
                  ),
                }}
              />
              {searchResults}
            </Box>
          )}
          {isMobile && <Box sx={{ flex: 1 }} />}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {!isMobile && ['licenses', 'systems', 'cameras'].includes(activeView) && (
              <Tooltip title="Split Screen">
                <IconButton
                  onClick={() => setSplitScreen(!splitScreen)}
                  sx={{
                    color: darkMode ? '#EAF3FB' : 'inherit',
                    bgcolor: splitScreen ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                  }}
                >
                  <ViewColumn />
                </IconButton>
              </Tooltip>
            )}

            {isMobile && (
              <Tooltip title="Search">
                <IconButton onClick={() => setMobileSearchOpen(true)} sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}>
                  <Search size={20} />
                </IconButton>
              </Tooltip>
            )}

            {!isMobile && (
              <Tooltip title="Refresh Data">
                <IconButton onClick={fetchAllData} disabled={refreshing} sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}>
                  <RefreshCw size={20} className={refreshing ? 'spin' : ''} />
                </IconButton>
              </Tooltip>
            )}

            {!isMobile && (
              <Tooltip title="Auto Refresh Settings">
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <IconButton
                    onClick={(e) => setAutoRefreshAnchor(e.currentTarget)}
                    sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}
                  >
                    <Clock size={20} />
                  </IconButton>
                  {autoRefresh && (
                    <Box sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      animation: 'pulse 2s ease-in-out infinite',
                    }} />
                  )}
                </Box>
              </Tooltip>
            )}

            <Tooltip title="Notifications">
              <IconButton
                onClick={(e) => setNotifAnchor(e.currentTarget)}
                sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}
              >
                <Badge badgeContent={notifCount} color="error" max={99}>
                  <BellIcon size={20} />
                </Badge>
              </IconButton>
            </Tooltip>

            {!isMobile && (
              <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                <IconButton
                  onClick={() => {
                    setDarkMode(!darkMode);
                    localStorage.setItem("darkMode", (!darkMode).toString());
                  }}
                  sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}
                >
                  {darkMode ? <Brightness7 /> : <Brightness4 />}
                </IconButton>
              </Tooltip>
            )}

            {isMobile && (
              <Tooltip title="More">
                <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}>
                  <MoreVert />
                </IconButton>
              </Tooltip>
            )}

            {!isMobile && currentUser === "admin" && activeView === "users" && (
              <Button
                variant="contained"
                startIcon={<ListBulletIcon />}
                onClick={fetchSessionLogs}
                sx={{ ml: 1 }}
              >
                View Logs
              </Button>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0.5, sm: 2 } }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                {currentUser.charAt(0).toUpperCase()}
              </Avatar>
              {!isMobile && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                    {currentUser}
                  </Typography>
                  <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                    {currentUser === 'admin' ? 'Administrator' : 'User'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Toolbar>
      )}

      {isMobile && mobileSearchOpen && searchResults}

      {/* Mobile overflow menu: refresh, auto-refresh, dark mode, view logs */}
      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <MenuItem
          onClick={() => {
            fetchAllData();
            setMoreAnchor(null);
          }}
          disabled={refreshing}
        >
          <ListItemIcon>
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </ListItemIcon>
          <ListItemText>Refresh data</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setAutoRefresh(!autoRefresh)}>
          <ListItemIcon>
            <Clock size={18} />
          </ListItemIcon>
          <ListItemText>Auto-refresh {autoRefresh ? `(every ${autoRefreshInterval}s)` : '(off)'}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDarkMode(!darkMode);
            localStorage.setItem("darkMode", (!darkMode).toString());
          }}
        >
          <ListItemIcon>
            {darkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{darkMode ? 'Light mode' : 'Dark mode'}</ListItemText>
        </MenuItem>
        {currentUser === "admin" && activeView === "users" && (
          <MenuItem
            onClick={() => {
              fetchSessionLogs();
              setMoreAnchor(null);
            }}
          >
            <ListItemIcon>
              <ListBulletIcon />
            </ListItemIcon>
            <ListItemText>View logs</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Auto Refresh Popover (desktop) */}
      <Menu
        anchorEl={autoRefreshAnchor}
        open={Boolean(autoRefreshAnchor)}
        onClose={() => setAutoRefreshAnchor(null)}
        PaperProps={{ sx: { p: 2, minWidth: 220 } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Auto Refresh
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              color="success"
              size="small"
            />
          }
          label={<Typography variant="body2">{autoRefresh ? 'Enabled' : 'Disabled'}</Typography>}
          sx={{ mb: 1.5, display: 'flex' }}
        />
        <FormControl size="small" fullWidth disabled={!autoRefresh}>
          <InputLabel>Interval</InputLabel>
          <Select
            value={autoRefreshInterval}
            onChange={(e) => setAutoRefreshInterval(e.target.value)}
            label="Interval"
          >
            <MenuItem value={15}>15 seconds</MenuItem>
            <MenuItem value={30}>30 seconds</MenuItem>
            <MenuItem value={60}>60 seconds</MenuItem>
            <MenuItem value={300}>5 minutes</MenuItem>
          </Select>
        </FormControl>
      </Menu>

      {/* Notification Popover */}
      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxWidth: 360,
            p: 2,
            bgcolor: darkMode ? '#0E1626' : 'background.paper',
            border: darkMode ? '1px solid #16213A' : '1px solid #D8E1E8',
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
          Notifications {notifCount > 0 && <Chip size="small" label={notifCount} color="error" sx={{ ml: 1 }} />}
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: darkMode ? '#93A2BC' : 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
          Expiring Licenses
        </Typography>
        {licenses
          .filter(l => { const d = Math.floor((new Date(l.end_date) - new Date()) / (1000 * 60 * 60 * 24)); return d >= 0 && d <= 30; })
          .slice(0, 3)
          .map(l => {
            const d = Math.floor((new Date(l.end_date) - new Date()) / (1000 * 60 * 60 * 24));
            return (
              <Box key={l.license_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>{l.license_id}</Typography>
                <Chip size="small" label={`${d}d`} sx={{ bgcolor: d <= 7 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1), color: d <= 7 ? 'error.main' : 'warning.main', fontWeight: 600 }} />
              </Box>
            );
          })}
        {expiringCount === 0 && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>No expiring licenses</Typography>}

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2, color: darkMode ? '#93A2BC' : 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
          Offline Cameras
        </Typography>
        {cameras
          .filter(c => c.status === 'offline')
          .slice(0, 3)
          .map(c => {
            const sys = getSystemDetails(systems, c.uuid);
            return (
              <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>{c.camera_serial}</Typography>
                <Chip size="small" label={sys?.project_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.grey[500], 0.1), color: 'text.secondary', fontWeight: 600 }} />
              </Box>
            );
          })}
        {offlineCameras === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>No offline cameras</Typography>}
      </Popover>
    </AppBar>
  );
}

export default TopBar;
